import { useRef, useState } from "react";
import axios from "axios";
import { ImagePlus, Trash2, Building2 } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Panel } from "@/components/admin/ui";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ALLOWED_TYPES = ["image/png", "image/jpeg", "image/webp"];
const MAX_BYTES = 10 * 1024 * 1024;

export const OrgBrandingSection = ({ logoUrl, onLogoChange }) => {
  const { API } = useAuth();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState("");

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError("");
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError("Please choose a PNG, JPG or WEBP image.");
      return;
    }
    if (file.size > MAX_BYTES) {
      setError("Logo image must be 10MB or smaller.");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await axios.post(`${API}/org/logo`, formData, { withCredentials: true });
      onLogoChange(data.logo_url);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    setRemoving(true);
    setError("");
    try {
      await axios.delete(`${API}/org/logo`, { withCredentials: true });
      onLogoChange(null);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setRemoving(false);
    }
  };

  return (
    <Panel title="Organization Branding" subtitle="A secondary logo shown across your organization's workspace." testId="org-settings-branding" className="flex h-full flex-col">
      <div className="flex flex-1 flex-col items-center justify-center gap-6 text-center">
        <div className="flex h-40 w-40 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 dark:border-white/15 dark:bg-white/5">
          {logoUrl ? (
            <img src={`${BACKEND_URL}${logoUrl}`} alt="Organization logo" data-testid="org-settings-logo-preview" className="h-full w-full object-contain" />
          ) : (
            <Building2 size={48} className="text-slate-300 dark:text-slate-600" data-testid="org-settings-logo-placeholder" />
          )}
        </div>
        <div className="flex flex-col items-center gap-2">
          <div className="flex flex-wrap justify-center gap-2">
            <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleFile} data-testid="org-settings-logo-file-input" />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="org-settings-logo-upload-btn"
              className="inline-flex items-center gap-2 rounded-xl bg-[#0052FF] px-4 py-2 text-xs font-bold text-white disabled:opacity-50"
            >
              <ImagePlus size={14} /> {uploading ? "Uploading…" : logoUrl ? "Replace Logo" : "Upload Logo"}
            </button>
            {logoUrl && (
              <button
                type="button"
                onClick={handleRemove}
                disabled={removing}
                data-testid="org-settings-logo-remove-btn"
                className="inline-flex items-center gap-2 rounded-xl border border-red-200 px-4 py-2 text-xs font-bold text-red-600 hover:bg-red-50 disabled:opacity-50 dark:border-red-500/30 dark:text-red-400 dark:hover:bg-red-500/10"
              >
                <Trash2 size={14} /> {removing ? "Removing…" : "Remove"}
              </button>
            )}
          </div>
          <p className="text-xs text-slate-400 dark:text-slate-500">PNG, JPG or WEBP · Up to 10MB · Optimized automatically</p>
          {error && <p className="text-xs font-semibold text-red-600 dark:text-red-400" data-testid="org-settings-logo-error">{error}</p>}
        </div>
      </div>
    </Panel>
  );
};
